import { HttpMethod, Type } from "@/Utils/request/types";
export interface StageSpec {
  workspace?: "general" | "reception" | "vitals" | "doctor";
  key: string;
  label: string;
  owner: string | null;
  optional: boolean;
  require_payment: boolean;
  require_observations: boolean;
}
export interface Workflow {
  id?: string;
  name: string;
  encounter_class: string;
  stages: StageSpec[];
  active: boolean;
  version: number;
}
export interface Choice {
  id: string;
  name: string;
  encounter_class?: string;
  patient_id?: string;
  date_of_birth?: string;
  created_at?: string;
}
export interface Visit {
  id: string;
  patient: string;
  patient_id: string;
  encounter: string;
  encounter_class: string;
  workflow: string;
  status: string;
  stage_index: number;
  stages: StageSpec[];
  version: number;
  practitioner: string;
  created_at: string;
  steps: {
    position: number;
    status: string;
    token: string;
    number: number;
    token_status: string;
    queue: string;
    owner: string;
  }[];
  workbench?: {
    handover?: {
      investigations?: {
        count: number;
        pending: number;
        orders: {
          id: string;
          title: string;
          status: string;
        }[];
      };
      reports?: Record<string, number>;
      medications?: Record<string, number>;
    };
    checks: { kind: string; ready: boolean }[];
    accounts: string[];
    observations_recorded: boolean;
    can_route: boolean;
    can_record: boolean;
    can_bill: boolean;
    consultation?: {
      notes: number;
      diagnoses: number;
      prescriptions: number;
      investigations: number;
      draft_orders: number;
    };
    can_read_clinical: boolean;
    next_stage: string | null;
  };
  events?: {
    action: string;
    actor: string;
    reason: string;
    details: { stage?: string };
    created_at: string;
  }[];
}
export interface List<T> {
  count: number;
  results: T[];
  can_manage?: boolean;
}
const base = "/api/visitflow/facilities/{facilityId}";
export const api = {
  workflows: {
    path: `${base}/workflows/`,
    method: HttpMethod.GET,
    TRes: Type<List<Workflow>>(),
  },
  save: {
    path: `${base}/workflows/`,
    method: HttpMethod.POST,
    TRes: Type<Workflow>(),
    TBody: Type<Workflow>(),
  },
  choices: {
    path: `${base}/choices/`,
    method: HttpMethod.GET,
    TRes: Type<List<Choice>>(),
  },
  visits: {
    path: `${base}/visits/`,
    method: HttpMethod.GET,
    TRes: Type<List<Visit>>(),
  },
  visit: {
    path: `${base}/visits/{id}/`,
    method: HttpMethod.GET,
    TRes: Type<Visit>(),
  },
  start: {
    path: `${base}/visits/`,
    method: HttpMethod.POST,
    TRes: Type<Visit>(),
    TBody: Type<{
      workflow: string;
      encounter: string;
      practitioner: string;
    }>(),
  },
  action: {
    path: `${base}/visits/{id}/`,
    method: HttpMethod.POST,
    TRes: Type<Visit>(),
    TBody: Type<{ action: string; version: number; reason: string }>(),
  },
};

export interface Arrival {
  id: string;
  receipt: string;
  name: string;
  contact: string;
  appointment_reference: string;
  patient_id?: string;
  booking_id?: string;
  status: string;
  created_at: string;
  version: number;
  visit: string | null;
}
export interface CheckInConfig {
  key: string;
  enabled: boolean;
  version: number;
}
export const checkInApi = {
  availableDoctors: {
    path: "/api/visitflow/facilities/{facilityId}/available-doctors/",
    method: HttpMethod.GET,
    TRes: Type<{
      date: string;
      count: number;
      results: {
        id: string;
        name: string;
        queue: string | null;
        start: string;
        end: string;
      }[];
    }>(),
  },
  publicInfo: {
    path: "/api/visitflow/check-in/{key}/",
    method: HttpMethod.GET,
    noAuth: true,
    TRes: Type<{ facility: string }>(),
  },
  submit: {
    path: "/api/visitflow/check-in/{key}/",
    method: HttpMethod.POST,
    noAuth: true,
    TRes: Type<{ receipt: string }>(),
    TBody: Type<{
      request_id: string;
      name: string;
      contact: string;
      appointment_reference: string;
    }>(),
  },
  config: {
    path: `${base}/check-in/`,
    method: HttpMethod.GET,
    TRes: Type<CheckInConfig>(),
  },
  saveConfig: {
    path: `${base}/check-in/`,
    method: HttpMethod.POST,
    TRes: Type<CheckInConfig>(),
    TBody: Type<{ enabled: boolean; version: number; rotate: boolean }>(),
  },
  arrivals: {
    path: `${base}/arrivals/`,
    method: HttpMethod.GET,
    TRes: Type<List<Arrival>>(),
  },
  resolve: {
    path: `${base}/arrivals/{id}/`,
    method: HttpMethod.POST,
    TRes: Type<Arrival>(),
    TBody: Type<{
      action: string;
      version: number;
      identity_verified?: boolean;
      encounter?: string;
      workflow?: string;
      practitioner?: string;
      reason?: string;
    }>(),
  },
};

export interface KioskOption {
  departments?: string[];
  doctor_count?: number;
  id: string;
  name?: string;
  doctor?: string;
  time?: string;
  status?: string;
  date_of_birth?: string;
}
export const kioskApi = {
  session: {
    path: "/api/visitflow/check-in/{key}/session/",
    method: HttpMethod.POST,
    noAuth: true,
    TRes: Type<{ access: string }>(),
    TBody: Type<{ phone_number: string }>(),
  },
  list: {
    path: "/api/visitflow/check-in/{key}/kiosk/",
    method: HttpMethod.GET,
    noAuth: true,
    TRes: Type<List<KioskOption>>(),
  },
  submit: {
    path: "/api/visitflow/check-in/{key}/kiosk/",
    method: HttpMethod.POST,
    noAuth: true,
    TRes: Type<{ receipt: string }>(),
    TBody: Type<{
      mode: string;
      booking_only?: boolean;
      request_id: string;
      booking?: string;
      patient?: string;
      slot?: string;
      name?: string;
      date_of_birth?: string;
      gender?: string;
    }>(),
  },
};

export const queueDisplayConfigApi = {
  path: "/api/visitflow/facilities/{facilityId}/queue-display/",
  method: HttpMethod.GET,
  TRes: Type<{
    rotation_seconds: number;
    hold_seconds: number;
    key: string;
    enabled: boolean;
    language: string;
    queues: string[];
    choices: { id: string; name: string }[];
  }>(),
};
